import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import Product from "./Product";
import PricePlan from "./PricePlan";
import Campaign from "./Campaign";
import Faq from "./Faq";
import SpecInfo from "./SpecInfo";
import CommonIssue from "./CommonIssue";
import Hardware from "./Hardware";
import Capabilities from "./Capabilities";
import CustomerStories from "./CustomerStories";
import Brands from "./Brands";
import OtherProducts from "./OtherProducts";
import ModalFileUpload from "../../components/ModalFileUpload/ModalFileUpload";
import CommonDeleteModal from "../../components/commonDeleteModal/CommonDeleteModal";
import CommonSuccessModal from "../../components/commonSuccessModal/CommonSuccessModal";

import {
  API_STATE,
  ASSET_LOCATION_MAPPING,
  ASSET_TYPE_MAPPING,
  LABEL_DELETE_ASSET,
  FORM_TYPE,
  STATUS_CODE_RESPONSE_OK,
  MESSAGE_ERROR_INPUT_REQUIRED,
  FILE_FIELD_MAPPING
} from "../../utils/constant";
import { isEmpty, isEmptyObj } from "../../utils/commonUtil";

import {
  fetchProduct,
  resetProductValue,
  setError,
  setValueBasicInfo,
  updateProduct,
} from "../../redux/productSlice";
import { deleteAsset, uploadAsset } from "../../redux/assetSlice";
import { createProducts } from "../../redux/productsSlice";
import { fetchLanguageOptions } from "../../redux/managePackageSlice"; // 添加导入

function ProductDetailEdit({
  productId,
  formType,
  initLanguage,
  handleComplete,
  handleBack,
}) {
  const dispatch = useDispatch();

  const TAB_PRODUCT_KEY = {
    basicInfo: "basicInfo",
    pricePlan: "pricePlan",
    campaign: "campaign",
    faq: "faq",
    specInfo: "specInfo",
    commonIssue: "commonIssue",
    hardware: "hardware",
    capabilities: "capabilities",
    customerStories: "customerStories",
    brands: "brands",
    otherProducts: "otherProducts",
  };

  const TAB_LIST_MAPPING = [
    {
      id: TAB_PRODUCT_KEY.basicInfo,
      label: "Basic Info",
      order: 1,
    },
    {
      id: TAB_PRODUCT_KEY.pricePlan,
      label: "Price Plan",
      order: 2,
    },
    /*
    {
      id: TAB_PRODUCT_KEY.campaign,
      label: "Campaign",
      order: 3,
    },
    */
    {
      id: TAB_PRODUCT_KEY.faq,
      label: "FAQ",
      order: 4,
    },
    {
      id: TAB_PRODUCT_KEY.specInfo,
      label: "Spec Info",
      order: 5,
    },
    {
      id: TAB_PRODUCT_KEY.commonIssue,
      label: "Common Issue",
      order: 6,
    },
    {
      id: TAB_PRODUCT_KEY.hardware,
      label: "Hardware",
      order: 7,
    },
    {
      id: TAB_PRODUCT_KEY.capabilities,
      label: "Capabilities",
      order: 8,
    },
    {
      id: TAB_PRODUCT_KEY.customerStories,
      label: "Customer Stories",
      order: 9,
    },
    {
      id: TAB_PRODUCT_KEY.brands,
      label: "Brands",
      order: 10,
    },
    {
      id: TAB_PRODUCT_KEY.otherProducts,
      label: "Other Products",
      order: 11,
    },
  ];

  const PRODUCT_MODEL_TEMPLATE = [
    {
      id: "menuName",
      label: "Menu name",
      placeholder: {
        EN: "Enter menu name in EN",
        CN: "Enter menu name in CN",
      },
      type: "text",
      required: true,
    },
    {
      id: "menuDescription",
      label: "Menu description",
      placeholder: {
        EN: "Enter menu description in EN",
        CN: "Enter menu description in CN",
      },
      type: "text",
    },
    {
      id: "subMenuName",
      label: "Sub menu name",
      placeholder: {
        EN: "Enter sub menu name in EN",
        CN: "Enter sub menu name in CN",
      },
      type: "text",
    },
    {
      id: "iconUrl",
      label: "Icon",
      placeholder: {
        EN: "Icon placeholder in EN",
        CN: "Icon placeholder in CN",
      },
      type: "file",
      acceptTypes: ".jpg,.jpeg,.png",
    },
    {
      id: "title",
      label: "Title",
      placeholder: {
        EN: "Enter title name in EN",
        CN: "Enter title name in CN",
      },
      type: "text",
      required: true,
    },
    {
      id: "description",
      label: "Description",
      placeholder: {
        EN: "Enter product description in EN",
        CN: "Enter product description in CN",
      },
      type: "text",
    },
    {
      id: "bannerUrl",
      label: "Banner",
      placeholder: {
        EN: "Enter banner in EN",
        CN: "Enter banner in CN",
      },
      type: "file",
      acceptTypes: ".jpg,.jpeg,.png",
    },
    {
      id: "poweredByLabel",
      label: "Powered By Label",
      placeholder: {
        EN: "Enter powered by label in EN",
        CN: "Enter powered by label in CN",
      },
      type: "text",
    },
    {
      id: "theme",
      label: "Theme",
      placeholder: {
        EN: "Enter theme color in EN",
        CN: "Enter theme color in CN",
      },
      type: "text",
      required: false,
    },
    {
      id: "videoIntroduction",
      label: "Video Introduction",
      placeholder: {
        EN: "Enter video URL",
        CN: "Enter video URL",
      },
      type: "text",
      required: false,
    }
  ];

  const productState = useSelector((state) => state.product);
  const { languageOptions } = useSelector((state) => state.managePackage);
  const product = productState.data || {};
  const status = productState.status;

  const [activeTab, setActiveTab] = useState(TAB_PRODUCT_KEY.basicInfo);
  const [language, setLanguage] = useState(initLanguage);
  const [isLoading, setIsLoading] = useState(false);
  const [isShowDeleteAsset, setIsShowDeleteAsset] = useState(null);
  const [isShowAlert, setIsShowAlert] = useState(null);
  const [isAlertSuccess, setIAlertSuccess] = useState(null);
  const [tabErrors, setTabErrors] = useState({});
  const pricePlanRef = useRef(null);
  const campaignRef = useRef(null);
  const faqRef = useRef(null);
  const specInfoRef = useRef(null);
  const commonIssueRef = useRef(null);
  const hardwareRef = useRef(null);
  const capabilitiesRef = useRef(null);
  const customerStoriesRef = useRef(null);
  const brandsRef = useRef(null);
  const otherProductsRef = useRef(null);

  useEffect(() => {
    if (formType === FORM_TYPE.EDIT) {
      dispatch(fetchProduct({ productId, language }));
    }
    if (formType === FORM_TYPE.CREATE) {
      dispatch(resetProductValue({ language }));
    }
    dispatch(fetchLanguageOptions());
  }, [formType, productId, language, dispatch]);

  const getLanguageOptions = () => {
    if (!languageOptions?.resdata?.data) return [];

    const options = [];
    const languageData = languageOptions.resdata.data;

    for (const langCode in languageData) {
      if (Array.isArray(languageData[langCode])) {
        languageData[langCode].forEach(item => {
          if (item.type === 'LANGUAGE' && item.codes?.length) {
            item.codes.forEach(codeItem => {
              options.push({
                value: codeItem.code,
                label: codeItem.value
              });
            });
          }
        });
      }
    }

    return options;
  };

  const languageOptionsForDisplay = getLanguageOptions();

  const toggleLanguage = (selectedLanguage) => {
    if (formType === FORM_TYPE.CREATE && selectedLanguage === "CN") {
      return;
    }
    setLanguage(selectedLanguage);
    if (formType === FORM_TYPE.EDIT) {
      dispatch(fetchProduct({ productId, language: selectedLanguage }));
    }
    if (formType === FORM_TYPE.CREATE) {
      dispatch(resetProductValue({ language: selectedLanguage }));
    }
  };

  const handleInputChangeBasicInfo = (key, newValue) => {
    dispatch(setValueBasicInfo({ key, value: newValue }));
  };

  const handleAdd = () => {
    if (activeTab === TAB_PRODUCT_KEY.pricePlan && pricePlanRef.current) {
      pricePlanRef.current.addGroup();
    } else if (activeTab === TAB_PRODUCT_KEY.campaign && campaignRef.current) {
      campaignRef.current.addGroup();
    } else if (activeTab === TAB_PRODUCT_KEY.faq && faqRef.current) {
      faqRef.current.addGroup();
    } else if (activeTab === TAB_PRODUCT_KEY.specInfo && specInfoRef.current) {
      specInfoRef.current.addGroup();
    } else if (activeTab === TAB_PRODUCT_KEY.commonIssue && commonIssueRef.current) {
      commonIssueRef.current.addGroup();
    } else if (activeTab === TAB_PRODUCT_KEY.hardware && hardwareRef.current) {
      hardwareRef.current.addGroup();
    } else if (activeTab === TAB_PRODUCT_KEY.capabilities && capabilitiesRef.current) {
      capabilitiesRef.current.addGroup();
    } else if (activeTab === TAB_PRODUCT_KEY.customerStories && customerStoriesRef.current) {
      customerStoriesRef.current.addGroup();
    } else if (activeTab === TAB_PRODUCT_KEY.brands && brandsRef.current) {
      brandsRef.current.addGroup();
    } else if (activeTab === TAB_PRODUCT_KEY.otherProducts && otherProductsRef.current) {
      otherProductsRef.current.addGroup();
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleSave = async () => {
    let benefitsErrors = {};
    let faqErrors = {};
    let specInfoErrors = {};
    let commonIssueErrors = {};

    if (faqRef.current) {
      faqErrors = faqRef.current.validateAllFaqs();
    }

    if (specInfoRef.current) {
      specInfoErrors = specInfoRef.current.validateAllSpecInfo();
    }

    if (commonIssueRef.current) {
      commonIssueErrors = commonIssueRef.current.validateAllCommonIssues();
    }

    let error = validationData();

    if (!isEmptyObj(benefitsErrors)) {
      Object.keys(benefitsErrors).forEach(index => {
        error[`pricePlan_${index}_benefits`] = benefitsErrors[index];
      });
    }

    if (!isEmptyObj(faqErrors)) {
      Object.keys(faqErrors).forEach(index => {
        error[`faq_${index}`] = faqErrors[index];
      });
    }

    if (!isEmptyObj(specInfoErrors)) {
      Object.keys(specInfoErrors).forEach(index => {
        error[`specInfo_${index}`] = specInfoErrors[index];
      });
    }

    if (!isEmptyObj(commonIssueErrors)) {
      Object.keys(commonIssueErrors).forEach(index => {
        error[`commonIssue_${index}`] = commonIssueErrors[index];
      });
    }

    if (isEmptyObj(error) && isEmptyObj(benefitsErrors) &&
      isEmptyObj(faqErrors) && isEmptyObj(specInfoErrors)) {
      dispatch(setError(null));
      setTabErrors({});
      setIsLoading(true);
      try {
        const topIntroduction = product.topIntroduction || {};

        const plans = product.plans || {};
        // const campaign = product.campaign || {};
        const faq = product.faq || { items: [] };
        const productSpec = product.productSpec || product.specInfo || { items: [] };
        const commonIssue = product.commonIssue || { items: [] };
        const hardware = product.hardware || { items: [] };
        const capabilities = product.capabilities || { items: [] };
        const customerStories = product.customerStories || { items: [] };
        const brands = product.brands || { src: [] };
        const otherProducts = product.otherProducts || { items: [] };

        const productData = {
          contentData: {
            topIntroduction: {
              menuName: topIntroduction.menuName || "",
              menuDescription: topIntroduction.menuDescription || "",
              subMenuName: topIntroduction.subMenuName || "",
              title: topIntroduction.title || "",
              description: topIntroduction.description || "",
              iconUrl: topIntroduction.iconUrl || "",
              iconFileId: topIntroduction.iconFileId || "",
              bannerUrl: topIntroduction.bannerUrl || "",
              bannerFileId: topIntroduction.bannerFileId || "",
            },
            poweredByLabel: topIntroduction.poweredByLabel || "",
            theme: topIntroduction.theme || "",
            videoIntroduction: topIntroduction.videoIntroduction || "",
            plans,
            // campaign,
            faq,
            productSpec,
            commonIssue,
            hardware,
            capabilities,
            customerStories,
            brands,
            otherProducts
          },
          region: product.region || "SG",
          language: language,
          languages: product.languages || ["EN", "CN"]
        };

        let response;

        if (formType === FORM_TYPE.EDIT) {
          response = await dispatch(
            updateProduct({ uuid: productId, language, payload: productData })
          );
        }

        if (formType === FORM_TYPE.CREATE) {
          response = await dispatch(createProducts(productData));
        }

        if (response?.payload?.status === STATUS_CODE_RESPONSE_OK) {
          handleAlertShow(
            formType === FORM_TYPE.EDIT
              ? "Product updated successfully!"
              : "Product created successfully!",
            true
          );
          setTimeout(() => {
            handleComplete();
          }, 1500);
        } else if (response?.error) {
          handleAlertShow(response.error.message, false);
        }
      } catch (err) {
        handleAlertShow(err.message || "An error occurred", false);
      } finally {
        setIsLoading(false);
      }
    } else {
      dispatch(setError(error));
      const newTabErrors = {};
      Object.keys(error).forEach(errorKey => {
        if (errorKey.startsWith('pricePlan_')) {
          newTabErrors[TAB_PRODUCT_KEY.pricePlan] = true;
        } else if (errorKey.startsWith('faq_')) {
          newTabErrors[TAB_PRODUCT_KEY.faq] = true;
        } else if (errorKey.startsWith('specInfo_')) {
          newTabErrors[TAB_PRODUCT_KEY.specInfo] = true;
        } else {
          newTabErrors[TAB_PRODUCT_KEY.basicInfo] = true;
        }
      });
      setTabErrors(newTabErrors);
    }
  };

  const handleAlertShow = (message, isSuccess) => {
    setIsShowAlert(message);
    setIAlertSuccess(isSuccess);
  };

  const handleAlertClose = () => {
    setIsShowAlert(null);
    setIAlertSuccess(null);
  };

  const validationData = () => {
    let error = {};
    PRODUCT_MODEL_TEMPLATE.forEach((template) => {
      if (template.required) {
        const topIntroduction = product.topIntroduction || {};
        const value = topIntroduction[template.id];
        if (isEmpty(value)) {
          error[template.id] = MESSAGE_ERROR_INPUT_REQUIRED;
        }
      }
    });

    const plansData = product.plans || {};
    const pricePlans = plansData.pricePlans || [];

    pricePlans.forEach((plan, index) => {
      if (isEmpty(plan.planTitle)) {

      }
    });

    return error;
  };

  // #region FILE UPLOAD
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState(null);

  const selectedKey = useRef(null);
  const selectedTab = useRef(null);

  const handleFileUpload = (file) => {
    if (!file) {
      setUploadErrorMessage("Please upload file.");
      return;
    }

    setIsLoading(true);

    const formData = new FormData();
    if (formType === FORM_TYPE.EDIT) {
      formData.append("uuid", productId);
    } else {
      formData.append("uuid", "");
    }

    formData.append(
      "assetLocation",
      ASSET_LOCATION_MAPPING[selectedKey.current]?.basicInfo
    );
    formData.append("assetType", ASSET_TYPE_MAPPING.product);
    formData.append("file", file);

    const response = dispatch(uploadAsset(formData));
    response
      .then((res) => {
        if (res?.payload) {
          const fileId = res.payload.resdata.fileId;
          const fileUrl = res.payload.resdata.url;

          if (selectedTab.current === TAB_PRODUCT_KEY.basicInfo) {
            const fieldKey = selectedKey.current;

            if (FILE_FIELD_MAPPING[fieldKey]) {
              const [urlField, fileIdField] = FILE_FIELD_MAPPING[fieldKey];
              handleInputChangeBasicInfo(urlField, fileUrl);
              handleInputChangeBasicInfo(fileIdField, fileId);
            } else {
              handleInputChangeBasicInfo(fieldKey, fileUrl);
              const fileIdField = `${fieldKey}FileId`;
              handleInputChangeBasicInfo(fileIdField, fileId);
            }
          }
        }
        if (res?.error) {
          handleAlertShow(res.error.message, false);
        }
        setIsLoading(false);
        setShowFileUploadModal(false);
        selectedKey.current = null;
        selectedTab.current = null;
      })
      .catch((err) => {
        handleAlertShow(err, false);
        setIsLoading(false);
        setShowFileUploadModal(false);
        selectedKey.current = null;
        selectedTab.current = null;
      });
  };

  const handleShowUploadFile = (id, tab) => {
    setShowFileUploadModal(true);
    selectedKey.current = id;
    selectedTab.current = tab;
  };

  const handleShowDeleteFile = (id, tab) => {
    setIsShowDeleteAsset(LABEL_DELETE_ASSET);
    selectedKey.current = id;
    selectedTab.current = tab;
  };

  const handleDeleteFile = () => {
    const topIntroduction = product.topIntroduction || {};
    const fieldKey = selectedKey.current;
    let fileId = '';
    if (FILE_FIELD_MAPPING[fieldKey]) {
      const [, fileIdField] = FILE_FIELD_MAPPING[fieldKey];
      fileId = topIntroduction[fileIdField] || topIntroduction[fieldKey];
    } else {
      fileId = topIntroduction[fieldKey];
    }

    const response = dispatch(deleteAsset(fileId));
    response
      .then((res) => {
        if (res?.payload || res?.error?.message?.includes("No active document")) {
          if (selectedTab.current === TAB_PRODUCT_KEY.basicInfo) {
            handleInputChangeBasicInfo(fieldKey, "");

            if (FILE_FIELD_MAPPING[fieldKey]) {
              const [, fileIdField] = FILE_FIELD_MAPPING[fieldKey];
              handleInputChangeBasicInfo(fileIdField, "");
            } else {
              handleInputChangeBasicInfo(`${fieldKey}FileId`, "");
            }
          }
        }
        if (res?.error && !res.error.message.includes("No active document")) {
          handleAlertShow(res.error.message, false);
        }

        setIsShowDeleteAsset(null);
        selectedKey.current = null;
        selectedTab.current = null;
      })
      .catch((err) => {
        handleAlertShow(err, false);
        setIsShowDeleteAsset(null);
        selectedKey.current = null;
        selectedTab.current = null;
      });
  };

  const handleCancelUploadFile = () => {
    setShowFileUploadModal(false);
    selectedKey.current = null;
  };
  // #endregion

  return (
    <div className="create-user-container">
      <div className="package-wrapper">
        <div className="package-section-title">
          {formType === FORM_TYPE.EDIT ? "Edit Product" : "Create Product"}
        </div>

        <div>
          <select
            className="language-select"
            value={language}
            onChange={(e) => toggleLanguage(e.target.value)}
            style={{
              padding: '1px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
              minWidth: '60px',
              minHeight: '30px',
              outline: 'none',
            }}
            disabled={formType === FORM_TYPE.CREATE && language === "CN"}
          >
            {languageOptionsForDisplay.map((option) => {
              const isDisabled = formType === FORM_TYPE.CREATE && (option.value === "CN" || option.value === "TW");
              return (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={isDisabled}
                >
                  {option.label}
                </option>
              );
            })}
          </select>
        </div>

        <div className="package-detail-edit-container">
          <div className="tabs-navigation">
            {TAB_LIST_MAPPING.map((tab, index) => (
              <button
                key={`tab_${index}`}
                className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => handleTabChange(tab.id)}
              >
                {tab.label}
                {tabErrors[tab.id] && <span style={{ color: 'red' }}>*</span>}
              </button>
            ))}
          </div>

          <div className="tab-content">
            {activeTab === TAB_PRODUCT_KEY.basicInfo &&
              status === API_STATE.SUCCEEDED && (
                <Product
                  templateList={PRODUCT_MODEL_TEMPLATE}
                  language={language}
                  onInputChange={handleInputChangeBasicInfo}
                  onShowUploadFile={(id) =>
                    handleShowUploadFile(id, TAB_PRODUCT_KEY.basicInfo)
                  }
                  onShowDeleteFile={(id) =>
                    handleShowDeleteFile(id, TAB_PRODUCT_KEY.basicInfo)
                  }
                />
              )}
            {activeTab === TAB_PRODUCT_KEY.pricePlan && (
              <PricePlan ref={pricePlanRef} language={language} />
            )}
            {/* {activeTab === TAB_PRODUCT_KEY.campaign && (
              <Campaign ref={campaignRef} language={language} productId={productId} formType={formType} onShowAlert={handleAlertShow} />
            ) */}
            {activeTab === TAB_PRODUCT_KEY.faq && (
              <Faq ref={faqRef} language={language} />
            )}
            {activeTab === TAB_PRODUCT_KEY.specInfo && (
              <SpecInfo ref={specInfoRef} language={language} productId={productId} formType={formType} onShowAlert={handleAlertShow} />
            )}
            {activeTab === TAB_PRODUCT_KEY.commonIssue && (
              <CommonIssue ref={commonIssueRef} language={language} />
            )}
            {activeTab === TAB_PRODUCT_KEY.hardware && (
              <Hardware ref={hardwareRef} language={language} productId={productId} formType={formType} onShowAlert={handleAlertShow} />
            )}
            {activeTab === TAB_PRODUCT_KEY.capabilities && (
              <Capabilities ref={capabilitiesRef} language={language} productId={productId} formType={formType} onShowAlert={handleAlertShow} />
            )}
            {activeTab === TAB_PRODUCT_KEY.customerStories && (
              <CustomerStories ref={customerStoriesRef} language={language} productId={productId} formType={formType} onShowAlert={handleAlertShow} />
            )}
            {activeTab === TAB_PRODUCT_KEY.brands && (
              <Brands ref={brandsRef} language={language} productId={productId} formType={formType} onShowAlert={handleAlertShow} />
            )}
            {activeTab === TAB_PRODUCT_KEY.otherProducts && (
              <OtherProducts ref={otherProductsRef} language={language} />
            )}
          </div>
        </div>

        <div className="button-group">
          <button
            className="package-detail-edit-back"
            disabled={isLoading}
            onClick={handleBack}
          >
            Back
          </button>

          {(activeTab === TAB_PRODUCT_KEY.pricePlan ||
            /* activeTab === TAB_PRODUCT_KEY.campaign || */
            activeTab === TAB_PRODUCT_KEY.faq ||
            activeTab === TAB_PRODUCT_KEY.specInfo ||
            activeTab === TAB_PRODUCT_KEY.commonIssue ||
            activeTab === TAB_PRODUCT_KEY.hardware ||
            activeTab === TAB_PRODUCT_KEY.capabilities ||
            activeTab === TAB_PRODUCT_KEY.customerStories ||
            activeTab === TAB_PRODUCT_KEY.brands ||
            activeTab === TAB_PRODUCT_KEY.otherProducts) && (
              <button
                className="package-detail-edit-add"
                disabled={isLoading}
                onClick={handleAdd}
              >
                Add new
              </button>
            )}

          <button
            className="package-detail-edit-save"
            disabled={isLoading}
            onClick={handleSave}
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <CommonDeleteModal
        show={!isEmpty(isShowDeleteAsset)}
        onHide={() => setIsShowDeleteAsset(null)}
        onConfirm={handleDeleteFile}
      />

      <CommonSuccessModal
        showModal={!isEmpty(isShowAlert)}
        handleClose={handleAlertClose}
        message={isShowAlert}
        status={isAlertSuccess}
      />

      <ModalFileUpload
        show={showFileUploadModal}
        handleCancel={handleCancelUploadFile}
        handleUpload={(file) => handleFileUpload(file)}
        isLoading={isLoading}
        errorMessage={uploadErrorMessage}
      />
    </div>
  );
}

export default ProductDetailEdit;
